import type { SSMClient } from '@aws-sdk/client-ssm';

import { ParameterCache, type ReadingSource } from './parameter-cache.js';

/**
 * Operational limits, changeable without a deployment (M0.2.4).
 *
 * These are the tuning knobs that stop a mistake becoming a bill: at alpha the
 * financial risk is a retry loop or a poll interval set in seconds, not growth
 * (Alpha-Design §8, CONTRIBUTING §7). Keeping them in SSM rather than in code
 * means a runaway can be throttled from the console in seconds.
 *
 * **These are not the emergency stop.** That is the kill switch, which fails
 * closed. These fall back to their compiled-in defaults when SSM cannot be
 * read, because a limit that silently becomes zero would stop the product
 * working for a reason nobody could see.
 */

export interface LimitSpec {
  /** Used when the parameter is absent, malformed, or out of bounds. */
  readonly fallback: number;
  /** Values below this are refused — a typo here is what costs money. */
  readonly min: number;
  /** Values above this are refused, to catch a misplaced digit. */
  readonly max: number;
}

/**
 * The four limits from [Alpha-Design §8](../../Technical/Alpha-Design.md),
 * with the bounds that make a typo non-catastrophic.
 */
export const LIMIT_SPECS = {
  /**
   * Minimum seconds between polls of a single feed. 15 minutes.
   *
   * The lower bound is the important number here: a poll interval accidentally
   * set in seconds is the single most expensive mistake available at alpha, and
   * is named as such in Alpha-Design §8. Alpha-Design also requires this to be
   * enforced in the scheduler rather than merely configured — that is M1's job;
   * this refuses to hand it a dangerous value in the first place.
   */
  pollIntervalFloorSeconds: { fallback: 900, min: 300, max: 86_400 },

  /** Cheap extractions per day. */
  dailyCheapExtractionBudget: { fallback: 200, min: 0, max: 10_000 },

  /** Browser renders per day — far more expensive than a cheap extraction. */
  dailyRenderBudget: { fallback: 20, min: 0, max: 1_000 },

  /** Subscriptions per user. 150, matching D17's trial ceiling. */
  feedCapPerUser: { fallback: 150, min: 1, max: 10_000 },
} as const satisfies Record<string, LimitSpec>;

export type LimitKey = keyof typeof LIMIT_SPECS;

export const LIMIT_KEYS = Object.keys(LIMIT_SPECS) as readonly LimitKey[];

export type OperationalLimits = Readonly<Record<LimitKey, number>>;

/** Why a limit is not the value in SSM. Surfaced so the caller can log it. */
export interface LimitRejection {
  readonly key: LimitKey;
  readonly rawValue: string | undefined;
  readonly reason: 'absent' | 'not-a-number' | 'out-of-bounds';
  readonly usedFallback: number;
}

export interface OperationalLimitsReading {
  readonly limits: OperationalLimits;
  readonly source: ReadingSource;
  /** Empty when every limit came from a well-formed parameter. */
  readonly rejections: readonly LimitRejection[];
  readonly error?: unknown;
}

export interface OperationalLimitsReaderOptions {
  /**
   * Maps each limit to its full SSM path. Supplied by the caller because
   * `shared` must not encode infrastructure layout — CDK passes the paths in as
   * environment variables.
   */
  readonly parameterNames: Readonly<Record<LimitKey, string>>;
  readonly client?: SSMClient;
  readonly cacheTtlMs?: number;
  readonly now?: () => number;
}

export class OperationalLimitsReader {
  private readonly parameterNames: Readonly<Record<LimitKey, string>>;
  private readonly cache: ParameterCache;

  constructor(options: OperationalLimitsReaderOptions) {
    this.parameterNames = options.parameterNames;
    // One GetParameters call for all four, rather than four round trips.
    this.cache = new ParameterCache(
      LIMIT_KEYS.map((key) => options.parameterNames[key]),
      {
        ...(options.client === undefined ? {} : { client: options.client }),
        ...(options.cacheTtlMs === undefined ? {} : { cacheTtlMs: options.cacheTtlMs }),
        ...(options.now === undefined ? {} : { now: options.now }),
      },
    );
  }

  async read(): Promise<OperationalLimitsReading> {
    const snapshot = await this.cache.read();

    const limits = {} as Record<LimitKey, number>;
    const rejections: LimitRejection[] = [];

    for (const key of LIMIT_KEYS) {
      const raw = snapshot.values.get(this.parameterNames[key]);
      const resolved = resolveLimit(key, raw);
      limits[key] = resolved.value;
      if (resolved.rejection !== undefined) {
        rejections.push(resolved.rejection);
      }
    }

    return {
      limits,
      source: snapshot.source,
      rejections,
      ...(snapshot.error === undefined ? {} : { error: snapshot.error }),
    };
  }
}

/**
 * Parses one limit, falling back to its default rather than trusting a value
 * that would be dangerous.
 *
 * Out-of-bounds values are **rejected, not clamped**. Clamping a poll floor of
 * `1` up to `300` would keep running while quietly disagreeing with what the
 * operator typed; falling back to the documented default and saying so is
 * easier to reason about at three in the morning.
 */
export function resolveLimit(
  key: LimitKey,
  rawValue: string | undefined,
): { value: number; rejection?: LimitRejection } {
  const spec: LimitSpec = LIMIT_SPECS[key];

  if (rawValue === undefined || rawValue.trim() === '') {
    return {
      value: spec.fallback,
      rejection: { key, rawValue, reason: 'absent', usedFallback: spec.fallback },
    };
  }

  const parsed = Number(rawValue.trim());
  if (!Number.isInteger(parsed)) {
    return {
      value: spec.fallback,
      rejection: { key, rawValue, reason: 'not-a-number', usedFallback: spec.fallback },
    };
  }

  if (parsed < spec.min || parsed > spec.max) {
    return {
      value: spec.fallback,
      rejection: { key, rawValue, reason: 'out-of-bounds', usedFallback: spec.fallback },
    };
  }

  return { value: parsed };
}
